/*
 * The master inbox's search, as the page holds it (master-inbox plan, step 3b): the operators the
 * rep has settled on, drawn as removable tokens in the field, and the words still being typed. The
 * API is the one parser (`src/inbox-v2/search.ts`); the page only writes operators in the form that
 * parser reads, and takes back what it parsed (`search.operators` on a page with `expand=list`).
 *
 * The filter chips, the typeahead and a saved view all write the same operators, so a campaign
 * chosen on a chip is the token `campaign-id:"…"` a campaign suggestion writes, and both come back
 * from a saved view as that token.
 */
import type { InboxPage } from "../../src/inbox-v2/contract.ts";

export type SearchOperator = NonNullable<InboxPage["search"]>["operators"][number];
type OperatorKind = SearchOperator["kind"];

/** A settled operator, with what to call its value where the value is an id or an address. */
export type SearchToken = SearchOperator & { readonly label?: string | undefined };

export const HAS_FILES: SearchToken = { kind: "has", value: "attachment" };

/** The operator as the API's parser reads it back: quoted, so a value with spaces stays one value. */
function operatorText(token: SearchOperator): string {
  return `${token.kind}:${JSON.stringify(token.value)}`;
}

/** What the API is asked: every token, then the words typed after them. */
export function composeQuery(tokens: readonly SearchOperator[], text: string): string {
  return [...tokens.map(operatorText), text.trim()].filter(Boolean).join(" ");
}

export const sameToken = (a: SearchOperator, b: SearchOperator): boolean =>
  a.kind === b.kind && a.value.toLowerCase() === b.value.toLowerCase();

/** The tokens with `token` added once; an operator already there is not written twice. */
export function withToken(tokens: readonly SearchToken[], token: SearchToken): SearchToken[] {
  return tokens.some((known) => sameToken(known, token)) ? [...tokens] : [...tokens, token];
}

export const withoutToken = (tokens: readonly SearchToken[], token: SearchOperator) =>
  tokens.filter((known) => !sameToken(known, token));

/** The campaign a token narrows to, by id: a chip's or a suggestion's choice. */
export const campaignToken = (tokens: readonly SearchToken[]) =>
  tokens.find((token) => token.kind === "campaign-id") ?? null;

/** The word the rep is typing now: what the typeahead completes. */
export function lastWord(text: string): string {
  return /(\S+)$/u.exec(text)?.[1] ?? "";
}

/** The words before the one a suggestion replaces. */
export function withoutLastWord(text: string): string {
  return text.replace(/\S+$/u, "").trimEnd();
}

/** A suggestion's exact operator (`from-address:"a@b.invalid"`), read back into a token. */
export function tokenFromQuery(query: string, label: string): SearchToken | null {
  const colon = query.indexOf(":");
  if (colon < 1) return null;
  const kind = KINDS.find((candidate) => candidate === query.slice(0, colon));
  const raw = query.slice(colon + 1);
  let value = raw;
  try {
    value = raw.startsWith('"') ? String(JSON.parse(raw)) : raw;
  } catch {
    return null;
  }
  return kind && value ? { kind, value, label } : null;
}

const KINDS: readonly OperatorKind[] = [
  "from",
  "from-address",
  "company",
  "campaign",
  "campaign-id",
  "mailbox",
  "mailbox-address",
  "has",
];

const KIND_NAMES: Record<OperatorKind, string> = {
  from: "From",
  "from-address": "From",
  company: "Company",
  campaign: "Campaign",
  "campaign-id": "Campaign",
  mailbox: "Mailbox",
  "mailbox-address": "Mailbox",
  has: "Has",
};

/**
 * What a token says: its kind as a word, and its value as the rep knows it. A campaign id is named
 * from the workspace's campaigns; `has:attachment` is one phrase.
 */
export function tokenText(
  token: SearchToken,
  campaigns: readonly { id: string; name: string }[],
): { kind: string; value: string } {
  if (token.kind === "has") return { kind: "Has", value: "files" };
  const named =
    token.kind === "campaign-id"
      ? campaigns.find((campaign) => campaign.id === token.value)?.name
      : undefined;
  return { kind: KIND_NAMES[token.kind], value: named ?? token.label ?? token.value };
}
